import { HTTP_INTERCEPTORS, HttpClient, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { ApplicationConfig, importProvidersFrom, inject, provideAppInitializer } from '@angular/core';
import { provideNativeDateAdapter } from '@angular/material/core';
import { provideRouter } from '@angular/router';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { NgxsReduxDevtoolsPluginModule } from '@ngxs/devtools-plugin';
import { provideStore } from '@ngxs/store';
import { ROLES } from '@shs-digi-care-monorepo/interfaces';
import { provideAuth } from 'angular-auth-oidc-client';
import { provideEchartsCore } from 'ngx-echarts';
import { APP_ROUTES } from './app.routes';
import { filter, Observable, switchMap } from 'rxjs';
import { AuthService, AUTH_ENVIRONMENT, TokenInjector } from '@shs-digi-care-monorepo/frontend-lib/auth';
import { environment } from '@shs-digi-care-monorepo/frontend-lib/environments';
import { UserState, TeamState } from '@shs-digi-care-monorepo/frontend-lib/states';
import { LoggingInterceptor, LOGGING_CONFIG } from '@shs-digi-care-monorepo/frontend-lib/logging';
import { provideShBreadcrumbRouting } from '@shs-digi-care-monorepo/frontend-lib/directives';
import { AllCommunityModule, ModuleRegistry } from 'ag-grid-community';

ModuleRegistry.registerModules([AllCommunityModule]);

// Helper function to create TranslateLoader
export function createTranslateLoader(http: HttpClient): TranslateHttpLoader {
    return new TranslateHttpLoader(http, '/assets/i18n/', '.json');
}

// App initialization factory function
function initializeAppFactory(authService: AuthService): () => Observable<boolean> {
    return () =>
        authService.isAuthenticated$.pipe(
            switchMap((isAuthenticated) => {
                if (!isAuthenticated) {
                    authService.login();

                    return authService.isAuthenticated$.pipe(filter((auth) => !!auth));
                }

                return new Observable<boolean>((observer) => {
                    observer.next(true);
                    observer.complete();
                });
            }),
            switchMap(() => new Observable<boolean>((observer) => observer.complete()))
        );
}

// ECharts initialization factory
async function initializeEChartsFactory(): Promise<unknown> {
    const [echarts, { BarChart, PieChart }, { TitleComponent, TooltipComponent, GridComponent }, { SVGRenderer }] =
        await Promise.all([
            import('echarts/core'),
            import('echarts/charts'),
            import('echarts/components'),
            import('echarts/renderers')
        ]);

    echarts.use([BarChart, PieChart, TitleComponent, TooltipComponent, GridComponent, SVGRenderer]);
    return echarts;
}

// Application config
export const appConfig: ApplicationConfig = {
    providers: [
        provideRouter(APP_ROUTES),
        importProvidersFrom(
            TranslateModule.forRoot({
                defaultLanguage: 'en',
                loader: {
                    provide: TranslateLoader,
                    useFactory: createTranslateLoader,
                    deps: [HttpClient]
                }
            }),
            NgxsReduxDevtoolsPluginModule.forRoot({ trace: true, disabled: environment.production })
        ),
        provideAuth({
            config: environment.authentication
        }),
        provideEchartsCore({ echarts: initializeEChartsFactory() }),
        provideStore([UserState, TeamState], {
            developmentMode: !environment.production,
            compatibility: { strictContentSecurityPolicy: true }
        }),
        provideAppInitializer(() => {
            const initializerFn = initializeAppFactory(inject(AuthService));
            return initializerFn();
        }),
        provideShBreadcrumbRouting(),
        {
            provide: 'ROLES',
            useValue: ROLES
        },
        {
            provide: 'RESOURCES',
            useValue: []
        },
        { provide: HTTP_INTERCEPTORS, useClass: TokenInjector, multi: true },
        { provide: HTTP_INTERCEPTORS, useClass: LoggingInterceptor, multi: true },
        { provide: AUTH_ENVIRONMENT, useValue: environment },
        { provide: LOGGING_CONFIG, useValue: { stage: environment.stage } },
        provideNativeDateAdapter(),
        provideHttpClient(withInterceptorsFromDi())
    ]
};
